import { RejectionCause, RejectionReason } from '@/store/Engine/constants';

export interface ModalState {
  showTable: boolean;
  showCompressor: boolean;
  showPossibleCauseCompressor: boolean;
  showFreeText: boolean;
  showPossibleCauseFreeText: boolean;
  isError: boolean;
  possibleCauseError: boolean;
  isReasonCompressorNotSelected: boolean;
  isCauseCompressorNotSelected: boolean;
  isTableNotSelected: boolean;
  selectedTable: string[];
  selectedCompressors: string[];
  selectedPossibleCauseCompressors: string[];
  commentText: string;
  causeCommentText: string;
}

export interface ValidationResult {
  isValid: boolean;
  errorMessage?: string;
  stateUpdates?: Partial<ModalState>;
  focusRef?: React.RefObject<HTMLTextAreaElement>;
}

export const initialState: ModalState = {
  showTable: false,
  showCompressor: false,
  showPossibleCauseCompressor: false,
  showFreeText: false,
  showPossibleCauseFreeText: false,
  isError: false,
  possibleCauseError: false,
  isReasonCompressorNotSelected: false,
  isCauseCompressorNotSelected: false,
  isTableNotSelected: false,
  selectedTable: [],
  selectedCompressors: [],
  selectedPossibleCauseCompressors: [],
  commentText: '',
  causeCommentText: '',
};

export const checkCompressorReason = (value: RejectionReason): Partial<ModalState> => {
  const compressorReasons = [RejectionReason.ProbabilityOfHarmingCompressorsHealth];
  return { showCompressor: compressorReasons.includes(value) };
};

export const checkCompressorCause = (value: RejectionCause): Partial<ModalState> => {
  const compressorReasons = [RejectionCause.SetpointsForCompressor];
  return { showPossibleCauseCompressor: compressorReasons.includes(value) };
};

export const checkTableCause = (value: RejectionCause): Partial<ModalState> => {
  const tableCauses = [
    RejectionCause.WrongCompressorCombinations,
    RejectionCause.PressureDrop,
    RejectionCause.SetpointsForCompressor,
  ];
  return { showTable: tableCauses.includes(value) };
};

export const checkFreeTextReason = (value: RejectionReason): Partial<ModalState> => {
  return { showFreeText: value === RejectionReason.Other };
};

export const checkFreeTextCause = (value: RejectionCause): Partial<ModalState> => {
  return { showPossibleCauseFreeText: value === RejectionCause.Other };
};

export const validateRejectionData = (
  reason: RejectionReason | null,
  cause: RejectionCause | null,
  state: ModalState,
  textAreaRef: React.RefObject<HTMLTextAreaElement>,
  possibleCauseTextAreaRef: React.RefObject<HTMLTextAreaElement>,
): ValidationResult[] => {
  const validations: ValidationResult[] = [];

  if (!reason) {
    validations.push({
      isValid: false,
      errorMessage: 'selectReasonError',
    });
  }

  if (!cause) {
    validations.push({
      isValid: false,
      errorMessage: 'selectCauseError',
    });
  }

  if (reason === RejectionReason.Other && !state.commentText.trim()) {
    validations.push({
      isValid: false,
      errorMessage: 'provideReason',
      stateUpdates: { isError: true },
      focusRef: textAreaRef,
    });
  }

  if (cause === RejectionCause.Other && !state.causeCommentText.trim()) {
    validations.push({
      isValid: false,
      errorMessage: 'provideReason',
      stateUpdates: { possibleCauseError: true },
      focusRef: possibleCauseTextAreaRef,
    });
  }

  if (state.showCompressor && !state.selectedCompressors.length) {
    validations.push({
      isValid: false,
      errorMessage: 'selectCompressorError',
      stateUpdates: { isReasonCompressorNotSelected: true },
    });
  }

  if (state.showPossibleCauseCompressor && !state.selectedPossibleCauseCompressors.length) {
    validations.push({
      isValid: false,
      errorMessage: 'selectCompressorCauseError',
      stateUpdates: { isCauseCompressorNotSelected: true },
    });
  }

  if (state.showTable && !state.selectedTable.length) {
    validations.push({
      isValid: false,
      errorMessage: 'selectTableError',
      stateUpdates: { isTableNotSelected: true },
    });
  }

  if (reason === RejectionReason.Other && state.commentText.trim()) {
    const comment = state.commentText.trim();
    if (comment.length < 2 || comment.length > 200) {
      validations.push({
        isValid: false,
        errorMessage: 'commentLengthError',
        stateUpdates: { isError: true },
        focusRef: textAreaRef,
      });
    }
  }

  if (cause === RejectionCause.Other && state.causeCommentText.trim()) {
    const causeComment = state.causeCommentText.trim();
    if (causeComment.length < 2 || causeComment.length > 200) {
      validations.push({
        isValid: false,
        errorMessage: 'provideReason',
        stateUpdates: { possibleCauseError: true },
        focusRef: possibleCauseTextAreaRef,
      });
    }
  }

  return validations;
};
